import { FormulaType } from '../constants/formulaType';

import {
    ALLOWED_CUSTOMER_IDS_FOR_CHANNEL_OVERRIDES,
    ChannelDefinition,
    ChannelOverrideMutationResult,
    ChannelRead,
    FieldSpec,
    MetadataFieldSpec,
    SchemaSource,
} from '../models/channelDefinition.model';

// content_cards is deprecated and excluded from the channel-override system
// entirely (composer-backend's app/channels/builtin.py) - GET /channels/content_cards
// 404s unconditionally, so there's no point ever querying it.
export const isOverridableChannelType = (
    formulaType: FormulaType | undefined,
): boolean => !!formulaType && formulaType !== FormulaType.contentCards;

// The 6 channel keys the override system supports: every FormulaType except
// the deprecated `content_cards`. Deliberately set membership rather than
// `isOverridableChannelType(key as FormulaType)` - a cast would wave through
// any unknown string (e.g. the retired `in_app_banner`/`in_app_fullpage`
// keys), which is exactly what this guard exists to exclude.
const OVERRIDABLE_CHANNEL_KEYS: ReadonlySet<string> = new Set(
    Object.values(FormulaType).filter(
        (type) => type !== FormulaType.contentCards,
    ),
);

export const isOverridableChannelKey = (key: string | undefined): boolean =>
    !!key && OVERRIDABLE_CHANNEL_KEYS.has(key);

// Pilot-rollout gate: only customers on the allowlist can see or use the
// channel-override management feature while it's being validated. This is
// a client-side visibility gate, not a security boundary - the backend's
// own permission checks remain authoritative.
export const canAccessChannelOverrides = (
    customerId: number | undefined,
): boolean =>
    !!customerId &&
    ALLOWED_CUSTOMER_IDS_FOR_CHANNEL_OVERRIDES.includes(customerId);

// After a force=true apply the backend reports which formulas it actually
// re-drafted and which it archived. The design doc requires the success
// toast to report those real counts rather than a static string. Note the
// backend's field names say "message", but per this feature's naming
// convention the user-facing noun is always "formula".
export const formatChannelOverrideSuccessMessage = (
    baseMessage: string,
    result: Pick<
        ChannelOverrideMutationResult,
        'drafted_message_ids' | 'archived_message_ids'
    >,
): string => {
    const draftedCount = (result.drafted_message_ids ?? []).length;
    const archivedCount = (result.archived_message_ids ?? []).length;

    const parts: string[] = [];
    if (draftedCount > 0) {
        parts.push(`${draftedCount} formula(s) drafted`);
    }
    if (archivedCount > 0) {
        parts.push(`${archivedCount} formula(s) archived`);
    }

    return parts.length > 0
        ? `${baseMessage} (${parts.join(', ')})`
        : baseMessage;
};

export const isOverrideChannelStructure = (
    structure: unknown,
): structure is { version: 2; fields: Record<string, unknown> } =>
    typeof structure === 'object' &&
    structure !== null &&
    !Array.isArray(structure);

export const getOverrideChannelFields = (
    structure: unknown,
): Record<string, unknown> | undefined => {
    if (!isOverrideChannelStructure(structure)) {
        return undefined;
    }
    return structure.fields;
};

// Matches composer-backend's `_is_override_channel` (app/structures/projection.py):
// only inline-schema overrides ever produce a raw v2 structure dict - an
// overridden channel with an external schema source is still converted to
// Slate before it reaches the frontend.
export const shouldUseChannelDefinitionView = (
    structure: unknown,
    channelRead: ChannelRead | undefined,
): boolean => {
    if (isOverrideChannelStructure(structure)) {
        return true;
    }
    return (
        channelRead?.overridden === true &&
        channelRead.effective.schema_source === SchemaSource.inline
    );
};

const isFieldValuePresent = (value: unknown): boolean => {
    if (value === undefined || value === null || value === '') {
        return false;
    }
    if (Array.isArray(value)) {
        return value.some(isFieldValuePresent);
    }
    return true;
};

const areFieldsSatisfied = (
    fields: FieldSpec[],
    values: Record<string, unknown> | undefined,
): boolean =>
    fields.every((field) => {
        const value = values?.[field.key];
        if (field.type === 'object') {
            const nested =
                value && typeof value === 'object' && !Array.isArray(value)
                    ? (value as Record<string, unknown>)
                    : undefined;
            if (field.required && !nested) {
                return false;
            }
            return areFieldsSatisfied(field.fields ?? [], nested);
        }
        return !field.required || isFieldValuePresent(value);
    });

// Required-field validation against the effective channel definition - the
// only validation possible for override-channel content, since there's no
// server-generated preview to check (that's what shouldUseChannelDefinitionView's
// content_format/schema_source distinction exists to avoid depending on).
export const validateOverrideChannelContent = (
    definition: ChannelDefinition | undefined,
    content: Record<string, unknown> | undefined,
): boolean => {
    if (!definition) {
        return false;
    }
    return areFieldsSatisfied(definition.fields ?? [], content);
};

export interface FieldTreeValidationError {
    path: string;
    message: string;
    // Set only on errors that are specifically about a field's `key` (empty,
    // malformed, duplicate, or colliding with another field/metadata key) so
    // the UI can attach them directly to the Key input's own error display,
    // rather than to a general per-row message.
    field?: 'key';
}

const MAX_FIELD_TREE_DEPTH = 4;

type LeafSpec = FieldSpec | MetadataFieldSpec;

// Matches composer-backend's key format constraint (app/channels/schema.py):
// lowercase letters, digits, hyphens, and underscores only. Checked here so
// an empty or malformed key is caught before a 422 round trip rather than
// only server-side.
const KEY_FORMAT = /^[a-z0-9_-]+$/;

const validateKeyFormat = (
    key: string,
    fieldPath: string,
    errors: FieldTreeValidationError[],
): void => {
    if (key.trim().length === 0) {
        errors.push({
            path: fieldPath,
            message: 'Key is required',
            field: 'key',
        });
    } else if (!KEY_FORMAT.test(key)) {
        errors.push({
            path: fieldPath,
            message:
                'Key must contain only lowercase letters, numbers, hyphens, and underscores',
            field: 'key',
        });
    }
};

const defaultTypeMismatch = (field: LeafSpec): boolean => {
    if (field.default === undefined || field.default === null) {
        return false;
    }
    const actualType =
        typeof field.default === 'boolean'
            ? 'bool'
            : typeof field.default === 'number'
            ? 'number'
            : typeof field.default === 'string'
            ? 'string'
            : 'object';
    return field.type !== actualType;
};

// Mirrors composer-backend's own field-tree validation (app/channels/schema.py:
// duplicate-sibling-key check, depth cap of 4, default-type match) closely
// enough to give fast client-side feedback before a 422 round trip. Not a
// byte-for-byte port - the server remains authoritative.
export const validateFieldTree = (
    fields: FieldSpec[],
    metadataFields: MetadataFieldSpec[] = [],
): FieldTreeValidationError[] => {
    const errors: FieldTreeValidationError[] = [];

    const walk = (nodes: FieldSpec[], path: string, depth: number) => {
        const seenKeys = new Set<string>();
        nodes.forEach((field) => {
            const fieldPath = path ? `${path}.${field.key}` : field.key;

            validateKeyFormat(field.key, fieldPath, errors);

            if (seenKeys.has(field.key)) {
                errors.push({
                    path: fieldPath,
                    message: `Duplicate field key "${field.key}"`,
                    field: 'key',
                });
            }
            seenKeys.add(field.key);

            if (defaultTypeMismatch(field)) {
                errors.push({
                    path: fieldPath,
                    message: `Default value for "${fieldPath}" must be a ${field.type}`,
                });
            }

            if (field.type === 'object') {
                const children = field.fields ?? [];
                if (depth >= MAX_FIELD_TREE_DEPTH && children.length > 0) {
                    errors.push({
                        path: fieldPath,
                        message: `"${fieldPath}" exceeds the maximum nesting depth of ${MAX_FIELD_TREE_DEPTH}`,
                    });
                } else {
                    walk(children, fieldPath, depth + 1);
                }
            }
        });
    };

    walk(fields, '', 1);

    const fieldKeys = new Set(fields.map((field) => field.key));
    const seenMetaKeys = new Set<string>();
    metadataFields.forEach((metaField) => {
        validateKeyFormat(metaField.key, metaField.key, errors);

        if (defaultTypeMismatch(metaField)) {
            errors.push({
                path: metaField.key,
                message: `Default value for "${metaField.key}" must be a ${metaField.type}`,
            });
        }
        if (fieldKeys.has(metaField.key)) {
            errors.push({
                path: metaField.key,
                message: `"${metaField.key}" is used by both fields and metadata_fields`,
                field: 'key',
            });
        }
        if (seenMetaKeys.has(metaField.key)) {
            errors.push({
                path: metaField.key,
                message: `Duplicate metadata field key "${metaField.key}"`,
                field: 'key',
            });
        }
        seenMetaKeys.add(metaField.key);
    });

    return errors;
};
